import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import withStyles from '@mui/styles/withStyles';
import useMediaQuery from "@mui/material/useMediaQuery";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { withPreviewQuery } from "common/preview";

import { sessionStatusMessage } from "business/session"
import classNames from 'classnames';
import { OngoingIcon } from "common/customIcon";
import { convertUrl, opaque, PAGE_HEIGHT_WITH_NAVBAR, transparent } from "common/helpers";

import { history } from 'common/history';
import MTMarkdown from "components/MTMarkdown";
import { find, get, isUndefined, join, toNumber, union } from "lodash";
import CancelIcon from '@mui/icons-material/Cancel';

import React from 'react';
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSetTranslation } from "common/useSetTranslation";

const MaterialStyles = theme => ({
  sessionIntroductionWrapper: {
    height: PAGE_HEIGHT_WITH_NAVBAR,
    display: 'flex',
    overflowY: 'hidden',
    alignItems: 'center',

  },
  sessionIntroDetailWrapper: {
    backgroundColor: '#DD6BAC',
    minHeight: 258,
    borderRadius: 9,
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    padding: '26px 16px 8px 16px'
  },
  sessionTitleContainer: {
    flex: 2,
    display: 'flex',
    flexDirection: 'column',
  },
  mobileSessionTitleContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  sessionTitle: {
    color: 'white',
    fontSize: '48px',
    flexGrow: 1,
    fontWeight: 'bold',
    fontFamily: 'Roboto',
    lineHeight: '56px'
  },
  mobileSessionTitle: {
    color: 'white',
    fontSize: '30px',
    fontWeight: 'bold'
  },
  sessionDescription: {
    color: 'black',
    flex: 2,
  },
  sessionActionsWrapper: {
    flex: '0 0 140px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    paddingBottom: '14px'
  },
  actionButtonsCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: 120,
  },
  startButton: {
    backgroundColor: 'white',
    borderRadius: 26,
    height: 44,
    minWidth: 120,
    padding: '0 20px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box'
  },
  startButtonLink: {
    textDecoration: 'none',
    color: 'black',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%'
  },
  previewButton: {
    backgroundColor: 'white',
    borderRadius: 26,
    height: 44,
    minWidth: 120,
    padding: '0 20px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    marginRight: 8,
    opacity: 0.8,
    transition: 'opacity 0.2s ease'
  },
  previewButtonLink: {
    textDecoration: 'none',
    color: 'black',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%'
  },
  tooltipDark: {
    backgroundColor: '#4A4A4A',
    color: '#FFFFFF',
    borderRadius: 12,
    padding: '12px 16px',
    fontSize: 14,
    lineHeight: '20px',
    maxWidth: 360
  },
  tooltipArrowDark: {
    color: '#4A4A4A'
  },
  confirmDialogTitle: {
    fontWeight: 700
  },
  confirmDialogContent: {
    paddingTop: 4,
    paddingBottom: 0
  },
  confirmPaper: {
    borderRadius: 16,
    padding: '8px 8px 16px 8px'
  },
  confirmBody: {
    marginTop: 0,
    fontSize: 16,
    lineHeight: '24px'
  },
  confirmActions: {
    padding: '8px 16px 8px 16px'
  },
  confirmBtnBase: {
    borderRadius: 24,
    height: 44,
    minWidth: 96,
    padding: '0 20px',
    fontWeight: 700
  },
  confirmBtnOutline: {
    border: '2px solid #E0B334',
    color: '#E0B334',
    backgroundColor: 'transparent'
  },
  confirmBtnFilled: {
    border: '2px solid #E0B334',
    backgroundColor: '#E0B334',
    color: '#FFFFFF',
    transition: 'all 0.3s ease',
    '&:hover': {
      color: '#E0B334'
    }
  },
  mobileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: PAGE_HEIGHT_WITH_NAVBAR
  },
  mobileSessionImageWrapper: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    minHeight: 250
  },
  mobileSessionContentWrapper: {
    flexDirection: 'column',
    flex: 1,
    backgroundColor: 'blue',
    padding: '2px 16px'
  },
  mobileBackArrow: {
    flex: 1,
  },
  mobileBookMarkWrapper: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    padding: 8
  }


});

const MentorshipSessionIntroduction = ({ classes, match, allSessions, ongoingSessions, toggleMentorshipBookmark, initSession, startSession, sessionStatus }) => {
  const sessionId = match.params.sessionId;
  const PREVIEW_BLOCK_STATES = ['ONGOING', 'PARTIAL_COMPLETE', 'COMPLETED'];

  const combinedSessions = union(ongoingSessions, allSessions);
  const thisSession = find(combinedSessions, (session) => session.id === toNumber(sessionId));

  const hasSessionStarted = () => !isUndefined(get(thisSession, "status")) ? get(thisSession, "status") === "ONGOING" : false;
  const isPreviewAllowed = () => !PREVIEW_BLOCK_STATES.includes((get(thisSession, 'status', '') || '').toUpperCase());

  const isMobileView = useMediaQuery('(max-width:600px)');
  const cardGradient = `linear-gradient(180deg,${transparent(thisSession.color)}  100px,${opaque(thisSession.color)}  300px),url(${convertUrl(thisSession.banner)})`;
  const { t } = useTranslation();
  const { st } = useSetTranslation();

  const [startConfirmOpen, setStartConfirmOpen] = React.useState(false);
  const openStartConfirm = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setStartConfirmOpen(true);
  };
  const handleStartConfirmNo = () => setStartConfirmOpen(false);
  const handleStartConfirmYes = async () => {
    try {
      await startSession(sessionId, false);
      await initSession(sessionId, false);
      history.replace(`/mentorship/${sessionId}/summary`);
    } finally {
      setStartConfirmOpen(false);
    }
  };

  // Prevent preview if session already started
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('mode') === 'preview' && !isPreviewAllowed()) {
      history.replace(`/mentorship/${sessionId}/summary`);
    }
  }, [sessionId, thisSession && thisSession.status]);

  const WebView = <div className={classNames(classes.sessionIntroductionWrapper)} style={{
    background: `url(${convertUrl(thisSession.banner)})`, backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover'
  }}>
    <Container maxWidth={"md"} disableGutters={true}
      style={{ backgroundColor: thisSession.color, position: 'relative' }}
      className={classNames(classes.sessionIntroDetailWrapper, 'animated faster zoomIn')}>
      <div style={{ position: 'absolute', top: '-16px', right: '-16px' }}>
        <IconButton size="large">
          <CancelIcon style={{ color: "#e0b334" }} fontSize={'large'} onClick={() => history.replace('/mentorship')} />
        </IconButton>
      </div>
      <div className={classes.sessionTitleContainer}>
        <div className={classes.sessionTitle}>{st(thisSession.title, thisSession.localeTitle)}</div>
        <div style={{ display: 'flex', marginBottom: 16 }}>
          <div style={{ color: 'rgba(255, 255, 255, 0.6)', flex: 1 }}>{join(thisSession.tags, ', ')}</div>
          <div style={{ flex: 2, paddingLeft: 16 }}>{sessionStatusMessage(thisSession, thisSession)}</div>
        </div>
      </div>
      <div className={classes.sessionDescription}>
        <MTMarkdown style={{
          fontSize: '14px',
          color: 'black',
        }}>{st(thisSession.overview, thisSession.localeOverview)}</MTMarkdown>
      </div>
      <div className={classes.sessionActionsWrapper}>
        <div style={{ flex: 2 }}>
          {thisSession && (
            thisSession.isBookmarked ? (
              <BookmarkIcon style={{ color: 'white', fontSize: '2rem' }}
                onClick={() => toggleMentorshipBookmark(thisSession.id)} />
            ) : (
              <BookmarkBorderIcon style={{ color: 'rgba(255,255,255,0.8)', fontSize: '2rem' }}
                onClick={() => toggleMentorshipBookmark(thisSession.id)} />
            )
          )}
        </div>
        <div className={classes.actionButtonsCol}>
          {isPreviewAllowed() && (
            <Tooltip title={t('previewButtonTooltipMessage')} placement="top" arrow
              classes={{ tooltip: classes.tooltipDark, arrow: classes.tooltipArrowDark }}>
              <div className={classes.previewButton}>
                <Link replace to={withPreviewQuery(`/mentorship/${sessionId}/summary`, true)}
                  className={classes.previewButtonLink}>
                  <span style={{ fontSize: 18 }}>{t('preview') || 'Preview'}</span>
                </Link>
              </div>
            </Tooltip>
          )}
          {!hasSessionStarted() ? (
            <Tooltip title={t('startButtonTooltipMessage')} placement="top" arrow
              classes={{ tooltip: classes.tooltipDark, arrow: classes.tooltipArrowDark }}>
              <div className={classes.startButton}>
                <Link replace to={`/mentorship/${sessionId}/summary`}
                  onClick={(e) => { e.preventDefault(); openStartConfirm(); }}
                  className={classes.startButtonLink}>
                  <span style={{ fontSize: 18 }}>{t('start')}</span>
                  <span><OngoingIcon viewBox="0 0 12 9" style={{ color: '#E0B334', marginLeft: 8 }} /></span>
                </Link>
              </div>
            </Tooltip>
          ) : (
            <div className={classes.startButton}>
              <Link replace to={`/mentorship/${sessionId}/summary`} className={classes.startButtonLink}>
                <span style={{ fontSize: 18 }}>{t('continue')}</span>
                <span><OngoingIcon viewBox="0 0 12 9" style={{ color: '#E0B334', marginLeft: 8 }} /></span>
              </Link>
            </div>
          )}

        </div>
      </div>
    </Container>
  </div>;

  const MobileView = (
    <div className={classes.mobileWrapper}>
      <div className={classes.mobileSessionImageWrapper}
        style={{ background: cardGradient, backgroundSize: 'cover' }}>
        <div className={classes.mobileBackArrow}>
          <IconButton size="large">
            <ArrowBackIcon style={{ color: "#52B2E2" }} fontSize={'large'}
              onClick={() => history.replace('/mentorship')} />
          </IconButton>
        </div>
        <div className={classes.mobileBookMarkWrapper}>
          {thisSession.isBookmarked ? (
            <BookmarkIcon style={{ color: 'white', fontSize: '2rem' }}
              onClick={() => toggleMentorshipBookmark(thisSession.id)} />
          ) : (
            <BookmarkBorderIcon style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '2rem' }}
              onClick={() => toggleMentorshipBookmark(thisSession.id)} />
          )}
        </div>
      </div>
      <div className={classes.mobileSessionContentWrapper} style={{ backgroundColor: thisSession.color }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', flexDirection: 'column', marginTop: 8, marginRight: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {isPreviewAllowed() && (
              <Tooltip
                title={t('previewButtonTooltipMessage')}
                placement="top"
                arrow
                classes={{ tooltip: classes.tooltipDark, arrow: classes.tooltipArrowDark }}
              >
                <div className={classes.previewButton} style={{ marginRight: 8 }}>
                  <Link
                    replace
                    to={withPreviewQuery(`/mentorship/${sessionId}/summary`, true)}
                    className={classes.previewButtonLink}
                  >
                    <span style={{ fontSize: 18 }}>{t('preview') || 'Preview'}</span>
                  </Link>
                </div>
              </Tooltip>
            )}

            <Tooltip
              title={t('startButtonTooltipMessage')}
              placement="top"
              arrow
              classes={{ tooltip: classes.tooltipDark, arrow: classes.tooltipArrowDark }}
            >
              <div className={classes.startButton}>
                <Link
                  replace
                  to={`/mentorship/${sessionId}/summary`}
                  onClick={(e) => { e.preventDefault(); openStartConfirm(); }}
                  className={classes.startButtonLink}
                >
                  <span style={{ fontSize: 18 }}>{hasSessionStarted() ? t('continue') : t('start')}</span>
                  <span>
                    <OngoingIcon viewBox="0 0 12 9" style={{ color: '#E0B334', marginLeft: '8px' }} />
                  </span>
                </Link>
              </div>
            </Tooltip>
          </div>
        </div>
        <div className={classes.mobileSessionTitleContainer}>
          <div className={classes.mobileSessionTitle}>{st(thisSession.title, thisSession.localeTitle)}</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ color: 'rgba(255, 255, 255, 0.6)', marginTop: 16, fontSize: 14 }}>
              {join(thisSession.tags, ', ')}
            </div>
            <div style={{ marginTop: 16, fontSize: 14 }}>
              {sessionStatusMessage(thisSession, thisSession)}
            </div>
          </div>
        </div>
        <div className={classes.sessionDescription}>
          <MTMarkdown style={{ fontSize: '14px', color: 'black' }}>
            {st(thisSession.overview, thisSession.localeOverview)}
          </MTMarkdown>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Dialog
        open={startConfirmOpen}
        onClose={handleStartConfirmNo}
        aria-labelledby="start-confirmation-title"
        PaperProps={{ className: classes.confirmPaper }}
      >
        <DialogTitle id="start-confirmation-title">
          <Typography variant="h6" className={classes.confirmDialogTitle}>
            {t('startConfirmTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.confirmDialogContent}>
          <Typography variant="body2" className={classes.confirmBody}>
            {t('startConfirmBody')}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.confirmActions}>
          <Button onClick={handleStartConfirmNo} className={classNames(classes.confirmBtnBase, classes.confirmBtnOutline)}>
            {t('no')}
          </Button>
          <Button onClick={handleStartConfirmYes} className={classNames(classes.confirmBtnBase, classes.confirmBtnFilled)} autoFocus>
            {t('yes')}
          </Button>
        </DialogActions>
      </Dialog>
      {isMobileView ? MobileView : WebView}

    </div>
  );
};

export default withStyles(MaterialStyles)(MentorshipSessionIntroduction);
